import { Trans } from 'react-i18next';
import styled from '@emotion/styled';
import { tokens, Banner } from '@hotjar/hotsauce';
import { useTrack } from '@hotjar/tracking';
import { getRoute } from '@hotjar/webapp/utils/routing';
import { BASE } from '@hotjar/webapp/constants/routes/overview';
import { useSites } from '@hotjar/site';
import { removeUserFromDemoSite } from '@hotjar/webapp/domains/activation/product-tour';

const Link = styled.a`
  color: ${tokens.color.text_default};
  text-decoration: underline;
`;

type DemoSiteAlertBannerProps = {
  isTrackingCodeInstalled: boolean;
  currentSiteId: number;
};

export const DemoSiteAlertBanner = ({
  isTrackingCodeInstalled,
  currentSiteId,
}: DemoSiteAlertBannerProps): JSX.Element => {
  const track = useTrack();
  const {
    query: { data: sites },
  } = useSites();
  const DEFAULT_SITE_ID = sites?.find((site) => site.id !== currentSiteId)?.id || currentSiteId;

  const lastVisitedSiteId = Number(localStorage.getItem('lastVisitedSiteId') || DEFAULT_SITE_ID);
  const lastVisitedSite = {
    href: getRoute(BASE.path, { siteId: lastVisitedSiteId }),
    linkLabel: 'activation:dashboard.demo-banner.link-aria-label',
  };

  const handleOnClick = (uiPosition: string) => {
    track.click('alert-banner', uiPosition, {});
    removeUserFromDemoSite();
  };

  return (
    <Banner>
      {isTrackingCodeInstalled ? (
        <Trans i18nKey="activation:dashboard.demo-banner.post-tci">
          This is the Hotjar demo site.{' '}
          <Link
            href={lastVisitedSite.href}
            onClick={() => handleOnClick('post-setup')}
            data-testid="alert-banner-link"
            aria-label={lastVisitedSite.linkLabel}
            target="_self"
          >
            Switch to your site
          </Link>
        </Trans>
      ) : (
        <Trans i18nKey="activation:dashboard.demo-banner.pre-tci">
          This is the Hotjar demo site. To get real insights from your site, install the tracking
          code.{' '}
          <Link
            href={lastVisitedSite.href}
            onClick={() => handleOnClick('pre-setup')}
            data-testid="alert-banner-link"
            aria-label={lastVisitedSite.linkLabel}
            target="_self"
          >
            Switch to your site
          </Link>
        </Trans>
      )}
    </Banner>
  );
};
